import React, { useEffect, useCallback, useState } from 'react';
import { Slider, NumberInput, Tooltip } from '@mantine/core';
import { useThrottleFn } from 'ahooks';

const emptyOnChange = () => {};

export const NumberInputGroup = React.memo((props) => {
  const {
    name,
    step = 1,
    min = 0,
    max = 100,
    inputWidth = 32,
    clampBehavior = 'none',
    allowDecimal,
    decimalScale,
    disabled,
    allowNegative = false,
    hideControls = false,
    value,
    suffix,
    onChange,
    styles,
    sliderLabel,
    throttleTime = 200,
    onSlideChange = () => {},
    tooltip,
    tooltipPosition,
    defaultValue,
  } = props;

  const [key, setKey] = useState(Date.now());
  const [tempValue, setTempValue] = useState(value);
  const onChangeThrottled = useThrottleFn(onChange || emptyOnChange, { wait: throttleTime });

  useEffect(() => {
    if (tempValue !== value) {
      setTempValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSlide = useCallback(
    (value) => {
      onChange && onChange(value, name);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, onChange],
  );

  const handleInput = useCallback(
    (value) => {
      if (value > max) {
        setKey(Date.now());
      }

      setTempValue(value);
      onChangeThrottled.run(Math.min(max, Math.max(min, value)), name);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, min, max],
  );

  useEffect(() => {
    setTempValue(value);
  }, [value, key]);

  return (
    <>
      <Slider
        size="xxs"
        min={min}
        defaultValue={defaultValue}
        max={max}
        step={step}
        label={sliderLabel}
        disabled={disabled}
        value={tempValue}
        onChange={(value) => {
          setTempValue(value);
          onSlideChange(value);
        }}
        onChangeEnd={handleSlide}
        className="flex-1 mini-number-slider"
      />
      <Tooltip label={tooltip} position={tooltipPosition || 'top'} disabled={!tooltip}>
        <NumberInput
          min={min}
          max={max}
          step={step}
          defaultValue={defaultValue}
          size="xxs"
          variant="filled"
          disabled={disabled}
          fixedDecimalScale
          w={inputWidth}
          value={tempValue}
          suffix={suffix}
          onChange={handleInput}
          clampBehavior={clampBehavior}
          allowDecimal={allowDecimal}
          decimalScale={decimalScale}
          allowNegative={allowNegative}
          hideControls={hideControls}
          className="mini-number-input ml-2 filled rounded text-mini text-left text-[#383838]"
          styles={styles?.number}
        />
      </Tooltip>
    </>
  );
});
